import moment from "moment";

/**
 * Fallback heuristics when a message has no schema-backed ``column_meta``
 * (older history). Live answers should carry labels/types from the AI service
 * schema generated at deploy time.
 */

/** Tokens that should render as uppercase acronyms in column headers. */
const ACRONYMS = new Set([
  "abn",
  "ah",
  "api",
  "bsb",
  "cos",
  "dob",
  "gst",
  "hr",
  "id",
  "km",
  "ndis",
  "pt",
  "sil",
  "sp",
  "url",
  "uuid",
]);

/** ISO / SQL date or datetime (optional fractional seconds + timezone). */
const DATE_LIKE_VALUE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Time-only values from Postgres TIME columns. */
const TIME_ONLY_RE = /^\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?$/;

/** Column names that strongly imply a calendar / clock value (legacy fallback). */
const DATE_COLUMN_RE = /(^|_)(at|date|on|time|dob|timestamp)s?$/i;

/**
 * Person / role name columns — values are title-cased for display
 * (DB often stores lowercase: ``anika yang`` → ``Anika Yang``).
 */
const PERSON_NAME_EXACT = new Set([
  "name",
  "first_name",
  "last_name",
  "full_name",
  "role",
  "role_name",
  "display_name",
  "emergency_contact_name",
  "participant",
  "participant_name",
  "worker",
  "worker_name",
  "employee_name",
  "client_name",
  "staff_name",
  "user_name",
  "provider_name",
]);
const PERSON_NAME_SUFFIX_RE = /_(first_name|last_name|full_name|role_name)$/i;

/** Exact header overrides when generic title-case is wrong (legacy fallback). */
const LABEL_OVERRIDES = {
  participant: "Participant Name",
  participant_name: "Participant Name",
  worker: "Worker Name",
  worker_name: "Worker Name",
};

const DATE_DISPLAY = "DD MMM YYYY";
const TIME_DISPLAY = "hh:mm A";
const DATETIME_DISPLAY = `${DATE_DISPLAY} ${TIME_DISPLAY}`;

/**
 * Title-case a person or role name for Copilot display.
 * ``anika yang`` → ``Anika Yang``, ``o'brien`` → ``O'Brien``.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function capitalizeDisplayName(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return text;
  if (text.includes("@") || text.includes("://")) return text;

  return text
    .split(/\s+/)
    .map((word) =>
      word
        .split(/([-'])/)
        .map((chunk) => {
          if (chunk === "-" || chunk === "'" || !chunk) return chunk;
          return chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
        })
        .join("")
    )
    .join(" ");
}

/**
 * @param {string} [column]
 * @returns {boolean}
 */
export function isPersonNameColumn(column) {
  if (!column) return false;
  const base = String(column).includes(".")
    ? String(column).split(".").pop()
    : String(column);
  const bare = base.toLowerCase();
  if (PERSON_NAME_EXACT.has(bare)) return true;
  return PERSON_NAME_SUFFIX_RE.test(bare);
}

/**
 * @param {unknown} columnMeta
 * @param {string} column
 * @returns {{ label?: string, type?: string } | null}
 */
function metaFor(columnMeta, column) {
  if (!columnMeta || typeof columnMeta !== "object") return null;
  const direct = columnMeta[column];
  if (direct && typeof direct === "object") return direct;
  if (typeof column === "string" && column.includes(".")) {
    const bare = column.split(".").pop();
    const nested = columnMeta[bare];
    if (nested && typeof nested === "object") return nested;
  }
  return null;
}

/**
 * Internal record identifiers — hide from Copilot chat tables / CSV.
 * Matches ``id`` / ``uuid`` / ``guid`` and any ``*_id`` / ``*_uuid`` column.
 * Business keys like ``ndis_number`` are kept.
 *
 * @param {unknown} column
 * @returns {boolean}
 */
export function isInternalIdentifierColumn(column) {
  if (column === null || column === undefined) return false;
  const raw = String(column).trim();
  if (!raw) return false;
  const bare = (raw.includes(".") ? raw.split(".").pop() : raw).toLowerCase();
  return (
    bare === "id" ||
    bare === "uuid" ||
    bare === "guid" ||
    bare.endsWith("_id") ||
    bare.endsWith("_uuid")
  );
}

/**
 * Drop id/uuid columns from a render projection (display / CSV only).
 * Keeps the original list if stripping would leave no columns.
 *
 * @param {string[]} columns
 * @returns {string[]}
 */
export function filterDisplayColumns(columns) {
  if (!Array.isArray(columns) || !columns.length) return [];
  const kept = columns.filter((col) => !isInternalIdentifierColumn(col));
  return kept.length ? kept : columns;
}

/**
 * Humanise a DB / SQL column name for chat table headers.
 * Prefer ``column_meta[col].label`` from the AI schema when present.
 *
 * @param {string} column
 * @param {Record<string, { label?: string, type?: string }> | null | undefined} [columnMeta]
 * @returns {string}
 */
export function formatColumnLabel(column, columnMeta) {
  const fromMeta = metaFor(columnMeta, column)?.label;
  if (typeof fromMeta === "string" && fromMeta.trim()) return fromMeta.trim();

  if (column === null || column === undefined) return "";
  const raw = String(column).trim();
  if (!raw) return "";

  // Drop table alias: p.first_name → first_name
  const base = raw.includes(".") ? raw.split(".").pop() : raw;
  const override = LABEL_OVERRIDES[String(base).toLowerCase()];
  if (override) return override;

  const parts = base
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .split("_")
    .filter(Boolean);

  if (!parts.length) return raw;

  return parts
    .map((part) => {
      const lower = part.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function looksLikeDateString(value) {
  if (typeof value !== "string") return false;
  return DATE_LIKE_VALUE_RE.test(value.trim());
}

/**
 * @param {string} [column]
 * @returns {boolean}
 */
function isDateColumn(column) {
  if (!column) return false;
  const base = String(column).includes(".")
    ? String(column).split(".").pop()
    : String(column);
  return DATE_COLUMN_RE.test(base);
}

/**
 * Schema / Postgres short types that should use date/time formatting.
 *
 * @param {string | undefined} type
 * @returns {"date" | "timestamp" | "time" | null}
 */
function displayTemporalKind(type) {
  const t = (type || "").toLowerCase();
  if (t === "date") return "date";
  if (t === "timestamp") return "timestamp";
  if (t === "time") return "time";
  return null;
}

/**
 * True when the source value includes a clock component (not date-only).
 *
 * @param {unknown} value
 * @param {import("moment").Moment} parsed
 * @returns {boolean}
 */
function valueHasTime(value, parsed) {
  if (value instanceof Date) {
    return (
      parsed.hours() !== 0 ||
      parsed.minutes() !== 0 ||
      parsed.seconds() !== 0 ||
      parsed.milliseconds() !== 0
    );
  }
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (TIME_ONLY_RE.test(trimmed)) return true;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  return /[T ]\d{2}:\d{2}/.test(trimmed);
}

/**
 * Parse table cell timestamps. Date-only strings stay calendar dates (no TZ
 * shift). Datetimes with an offset use that offset; naive datetimes follow the
 * same UTC→local convention as chat message timestamps.
 *
 * @param {unknown} value
 * @returns {import("moment").Moment | null}
 */
export function parseTableDateValue(value) {
  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m : null;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (TIME_ONLY_RE.test(trimmed)) {
    const m = moment(trimmed, ["HH:mm:ss.SSSSSS", "HH:mm:ss", "HH:mm"], true);
    return m.isValid() ? m : null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const m = moment(trimmed, "YYYY-MM-DD", true);
    return m.isValid() ? m : null;
  }

  if (!DATE_LIKE_VALUE_RE.test(trimmed)) return null;

  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
    const m = moment(trimmed);
    return m.isValid() ? m : null;
  }

  // Naive SQL/ISO datetime from the API — treat as UTC then show local.
  const normalised = trimmed.replace("T", " ").replace(/\.\d+$/, "");
  const m = moment.utc(normalised, "YYYY-MM-DD HH:mm:ss", true).local();
  return m.isValid() ? m : null;
}

/**
 * Format a date/time cell: `01 Jan 2025` or `01 Jan 2025 03:45 PM`.
 *
 * @param {unknown} value
 * @param {"date" | "timestamp" | "time" | null} [kind]
 * @returns {string | null}
 */
export function formatTableDateValue(value, kind = null) {
  const parsed = parseTableDateValue(value);
  if (!parsed) return null;

  if (kind === "time" || (typeof value === "string" && TIME_ONLY_RE.test(value.trim()))) {
    return parsed.format(TIME_DISPLAY);
  }
  if (kind === "date") {
    // Calendar date columns must not shift across days via timezone.
    if (typeof value === "string") {
      const day = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
      if (day) {
        const calendar = moment(day[1], "YYYY-MM-DD", true);
        if (calendar.isValid()) return calendar.format(DATE_DISPLAY);
      }
    }
    return parsed.format(DATE_DISPLAY);
  }
  if (kind === "timestamp" || valueHasTime(value, parsed)) {
    return parsed.format(DATETIME_DISPLAY);
  }
  return parsed.format(DATE_DISPLAY);
}

/**
 * Bare column name (strip table alias).
 *
 * @param {string} [column]
 * @returns {string}
 */
function bareColumnName(column) {
  if (!column) return "";
  const raw = String(column);
  return (raw.includes(".") ? raw.split(".").pop() : raw).toLowerCase();
}

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/**
 * Parse JSON / Python-repr structured cell values (dicts, lists).
 *
 * @param {unknown} value
 * @returns {unknown}
 */
export function parseStructuredCellValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    // Older rows used Python str(dict) — single quotes + None/True/False.
    try {
      const normalised = trimmed
        .replace(/\bNone\b/g, "null")
        .replace(/\bTrue\b/g, "true")
        .replace(/\bFalse\b/g, "false")
        .replace(/'/g, '"');
      return JSON.parse(normalised);
    } catch {
      return null;
    }
  }
}

/**
 * @param {unknown} slots
 * @returns {string[]}
 */
function availabilitySlotParts(slots) {
  if (!Array.isArray(slots)) {
    if (slots && typeof slots === "object") {
      const from = slots.from ?? slots.start ?? null;
      const to = slots.to ?? slots.end ?? null;
      if (from && to) return [`${from}–${to}`];
    }
    if (typeof slots === "string" && slots.trim()) return [slots.trim()];
    return [];
  }
  const parts = [];
  for (const slot of slots) {
    if (!slot || typeof slot !== "object") {
      if (typeof slot === "string" && slot.trim()) parts.push(slot.trim());
      continue;
    }
    const from = slot.from ?? slot.start ?? null;
    const to = slot.to ?? slot.end ?? null;
    if (from && to) parts.push(`${from}–${to}`);
  }
  return parts;
}

/**
 * Weekly availability JSON → readable day windows.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export function formatAvailabilityCell(value) {
  const decoded = parseStructuredCellValue(value);
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
    return null;
  }
  const keys = Object.keys(decoded);
  const looksLikeWeek = keys.some((k) => WEEKDAY_ORDER.includes(k.toLowerCase()));
  if (!looksLikeWeek) return null;

  const bits = [];
  for (const day of WEEKDAY_ORDER) {
    const matchKey = keys.find((k) => k.toLowerCase() === day);
    if (!matchKey) continue;
    const parts = availabilitySlotParts(decoded[matchKey]);
    if (!parts.length) continue;
    bits.push(`${day.charAt(0).toUpperCase() + day.slice(1, 3)} ${parts.join(", ")}`);
  }
  return bits.length ? bits.join("; ") : "No open day slots";
}

/**
 * Key strengths JSON (list of {Name: score} or map) → readable labels.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export function formatKeyStrengthsCell(value) {
  const decoded = parseStructuredCellValue(value);
  if (decoded == null) return null;

  const bits = [];
  if (Array.isArray(decoded)) {
    for (const item of decoded) {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        for (const [k, v] of Object.entries(item)) {
          bits.push(v == null || v === "" ? String(k) : `${k} (${v})`);
        }
      } else if (item != null && item !== "") {
        bits.push(String(item));
      }
    }
  } else if (typeof decoded === "object") {
    for (const [k, v] of Object.entries(decoded)) {
      bits.push(v == null || v === "" ? String(k) : `${k} (${v})`);
    }
  } else {
    return null;
  }
  return bits.length ? bits.join(", ") : "—";
}

/**
 * Generic object/array → compact readable text (not raw JSON dumps).
 *
 * @param {unknown} value
 * @returns {string}
 */
function formatGenericStructuredCell(value) {
  const decoded = parseStructuredCellValue(value);
  if (decoded == null) {
    return typeof value === "string" ? value : String(value);
  }
  if (Array.isArray(decoded)) {
    if (!decoded.length) return "—";
    return decoded
      .map((item) => {
        if (item && typeof item === "object" && !Array.isArray(item)) {
          return Object.entries(item)
            .map(([k, v]) => (v == null || v === "" ? k : `${k}: ${v}`))
            .join(", ");
        }
        return String(item);
      })
      .filter(Boolean)
      .join("; ");
  }
  if (typeof decoded === "object") {
    const entries = Object.entries(decoded);
    if (!entries.length) return "—";
    return entries
      .map(([k, v]) => {
        if (v == null || v === "") return k;
        if (typeof v === "object") {
          try {
            return `${k}: ${JSON.stringify(v)}`;
          } catch {
            return `${k}: ${String(v)}`;
          }
        }
        return `${k}: ${v}`;
      })
      .join("; ");
  }
  return String(decoded);
}

/**
 * Present a scalar cell value for the Copilot SQL table.
 *
 * Prefer schema ``column_meta[col].type`` from the AI service when present so
 * formatting stays aligned with deploy-time schema generation.
 *
 * @param {unknown} value
 * @param {string} [column]
 * @param {Record<string, { label?: string, type?: string }> | null | undefined} [columnMeta]
 * @returns {string}
 */
export function formatTableCell(value, column, columnMeta) {
  if (value === null || value === undefined) return "—";

  const meta = metaFor(columnMeta, column);
  const schemaType = (meta?.type || "").toLowerCase();
  const colKey = bareColumnName(column);

  // Permission portal types: blue_portal → Blue Portal.
  if (
    colKey === "permission_type" &&
    typeof value === "string" &&
    /^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(value.trim())
  ) {
    return value
      .trim()
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }

  if (typeof value === "boolean" || schemaType === "bool") {
    // Employment/participant status columns are Active/Inactive; other bools Yes/No.
    const isStatusCol =
      colKey === "status" ||
      colKey.endsWith("_status");
    const truthy = value === true || value === "true" || value === "True";
    const falsy = value === false || value === "false" || value === "False";
    if (typeof value === "boolean" || truthy || falsy) {
      if (isStatusCol) return truthy ? "Active" : "Inactive";
      return truthy ? "Yes" : "No";
    }
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "—";
    return String(value);
  }

  // JSON / structured HRM fields (availability, strengths, etc.).
  const isAvailabilityCol =
    colKey === "availability" || colKey.endsWith("_availability");
  const isStrengthsCol =
    colKey === "key_strengths" ||
    colKey === "strengths" ||
    colKey.endsWith("_strengths");

  if (isAvailabilityCol) {
    const pretty = formatAvailabilityCell(value);
    if (pretty) return pretty;
  }
  if (isStrengthsCol) {
    const pretty = formatKeyStrengthsCell(value);
    if (pretty) return pretty;
  }

  if (typeof value === "object") {
    if (value instanceof Date) {
      return formatTableDateValue(value, displayTemporalKind(schemaType)) || "—";
    }
    const avail = formatAvailabilityCell(value);
    if (avail) return avail;
    const strengths = formatKeyStrengthsCell(value);
    if (strengths && Array.isArray(value)) return strengths;
    return formatGenericStructuredCell(value);
  }

  const trimmed = String(value).trim();
  if (!trimmed) return "—";

  // Structured string payloads (Python repr / JSON text from older rows).
  if (trimmed[0] === "{" || trimmed[0] === "[") {
    const avail = formatAvailabilityCell(trimmed);
    if (avail) return avail;
    if (isStrengthsCol || trimmed[0] === "[") {
      const strengths = formatKeyStrengthsCell(trimmed);
      if (strengths && strengths !== "—") return strengths;
    }
    return formatGenericStructuredCell(trimmed);
  }

  {
    const isStatusCol =
      colKey === "status" || colKey.endsWith("_status");
    if (trimmed === "true" || trimmed === "True") {
      return isStatusCol ? "Active" : "Yes";
    }
    if (trimmed === "false" || trimmed === "False") {
      return isStatusCol ? "Inactive" : "No";
    }
  }

  const temporalKind = displayTemporalKind(schemaType);
  const shouldTryDate =
    temporalKind != null ||
    looksLikeDateString(trimmed) ||
    isDateColumn(column);

  if (shouldTryDate) {
    const formatted = formatTableDateValue(trimmed, temporalKind);
    if (formatted) return formatted;
  }

  if (isPersonNameColumn(column)) {
    return capitalizeDisplayName(trimmed);
  }

  return trimmed;
}
